import type { CategoryNode } from './types';

/**
 * The walks the category surface needs, in one place.
 *
 * None of this is drawn by the mockup — a static tree has nothing to walk — so
 * all of it is application logic authored against what the payload is and what
 * the parent field has to offer.
 *
 * **The separator is the pack's own `›`**, and the full path is what the parent
 * field lists: «دراما › تاريخي» and never «تاريخي». Two children under different
 * parents may carry the same name, and a bare name cannot tell them apart — a
 * field that offered one would let an operator pick a parent they did not
 * choose, silently, and the tree would move a branch under the wrong node.
 */

export const PATH_SEPARATOR = ' › ';

/**
 * One `/api/v1/categories` row, as the service actually serves it.
 *
 * **The tree arrives flat.** `GET /api/v1/categories` returns `data` as a bare
 * array of table rows carrying `parent_id`, `materialized_path`, `depth` and
 * `position` — not a nested payload — so «full depth, ordered by `position`
 * within each parent» describes the ROWS, and nesting them is this console's
 * work. Every reader on this surface walks `children`, so the rebuild happens
 * once, here, at the edge of the query layer.
 */
export interface WireCategoryRow {
  id: string;
  parent_id: string | null;
  slug: string;
  name: string;
  depth: number;
  position: number;
}

/**
 * Nest the flat rows into the tree every walk on this surface expects.
 *
 * Siblings keep the service's `position` order — the rank an operator set — and
 * the rows are only sorted within a parent, never globally, so the payload's own
 * ordering of unrelated branches is left alone.
 *
 * A row whose `parent_id` names nothing in the payload is promoted to the root
 * rather than dropped. It can only mean a parent the caller was not allowed to
 * see or a row mid-move, and a genre that vanishes from the console is worse
 * than one that shows up one level too high: the operator can see the second and
 * fix it, and cannot see the first at all.
 */
export function composeCategoryTree(rows: readonly WireCategoryRow[]): CategoryNode[] {
  const nodes = new Map<string, CategoryNode>();
  for (const row of rows) {
    nodes.set(row.id, { id: row.id, name: row.name, slug: row.slug, position: row.position, children: [] });
  }

  const roots: CategoryNode[] = [];
  for (const row of rows) {
    const node = nodes.get(row.id);
    if (!node) continue;
    const parent = row.parent_id === null ? undefined : nodes.get(row.parent_id);
    if (parent) parent.children?.push(node);
    else roots.push(node);
  }

  function rank(siblings: CategoryNode[]): CategoryNode[] {
    siblings.sort((left, right) => left.position - right.position);
    for (const sibling of siblings) rank(sibling.children ?? []);
    return siblings;
  }

  return rank(roots);
}

/** A leaf's `children` may be absent rather than empty; every walk goes here. */
export function childrenOf(node: CategoryNode): readonly CategoryNode[] {
  return node.children ?? [];
}

export function hasChildren(node: CategoryNode): boolean {
  return childrenOf(node).length > 0;
}

/** One node of the tree, flattened with the path the parent field displays. */
export interface FlatCategory {
  id: string;
  name: string;
  /** Ancestors then self, joined by `›`. */
  path: string;
  depth: number;
  parentId: string | null;
}

/**
 * Depth-first, parents before children, siblings in payload order.
 *
 * Payload order is the service's `position` within each parent, so the list is
 * already ranked and this walk must not re-sort it: a sort here would present
 * the operator an order the tree does not have.
 */
export function flattenTree(nodes: readonly CategoryNode[]): FlatCategory[] {
  const flat: FlatCategory[] = [];

  function walk(current: readonly CategoryNode[], parentId: string | null, ancestry: string[]) {
    for (const node of current) {
      const trail = [...ancestry, node.name];
      flat.push({
        id: node.id,
        name: node.name,
        path: trail.join(PATH_SEPARATOR),
        depth: trail.length - 1,
        parentId,
      });
      walk(childrenOf(node), node.id, trail);
    }
  }

  walk(nodes, null, []);
  return flat;
}

/** The node and everything beneath it — the set the parent field must exclude. */
export function selfAndDescendantIds(nodes: readonly CategoryNode[], termId: string): string[] {
  const subtree = findNode(nodes, termId);
  if (!subtree) return [termId];

  const ids: string[] = [];

  function collect(node: CategoryNode) {
    ids.push(node.id);
    for (const child of childrenOf(node)) collect(child);
  }

  collect(subtree);
  return ids;
}

export function findNode(nodes: readonly CategoryNode[], termId: string): CategoryNode | null {
  for (const node of nodes) {
    if (node.id === termId) return node;
    const found = findNode(childrenOf(node), termId);
    if (found) return found;
  }
  return null;
}

/**
 * The parent the edit dialog opens on.
 *
 * The mockup leaves this at the root in every mode, because the demo layer does
 * not know a node's parent. The real one does, and showing an operator «بلا أب»
 * for a node that has one would offer to promote it to the top level as the
 * default action of pressing save.
 */
export function parentIdOf(nodes: readonly CategoryNode[], termId: string): string | null {
  return flattenTree(nodes).find((node) => node.id === termId)?.parentId ?? null;
}

export function countNodes(nodes: readonly CategoryNode[]): number {
  return flattenTree(nodes).length;
}
